import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import CTASection from '../components/CTASection';
import Button from '../components/Button';
import './Gallery.css';

const Gallery: React.FC = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);

  // Theme data with background colors - memoized to prevent recreation
  const themes = useMemo(() => [
    { id: 'princess', icon: '👑', bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)' },
    { id: 'unicorn', icon: '🦄', bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 50%, #d299c2 100%)' },
    { id: 'pokemon', icon: '⚡', bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 50%, #ff8a80 100%)' },
    { id: 'superhero', icon: '🦸', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' },
    { id: 'space', icon: '🚀', bg: 'linear-gradient(135deg, #2c3e50 0%, #3498db 50%, #9b59b6 100%)' },
    { id: 'mermaid', icon: '🧜‍♀️', bg: 'linear-gradient(135deg, #00c9ff 0%, #92fe9d 50%, #00c9ff 100%)' },
    { id: 'dinosaur', icon: '🦕', bg: 'linear-gradient(135deg, #8bc34a 0%, #4caf50 50%, #2e7d32 100%)' },
    { id: 'fairy', icon: '🧚‍♀️', bg: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 50%, #a8edea 100%)' },
    { id: 'pirate', icon: '🏴‍☠️', bg: 'linear-gradient(135deg, #8b4513 0%, #d2691e 50%, #ffd700 100%)' },
    { id: 'minecraft', icon: '⛏️', bg: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 50%, #cddc39 100%)' },
    { id: 'frozen', icon: '❄️', bg: 'linear-gradient(135deg, #e3f2fd 0%, #81d4fa 50%, #29b6f6 100%)' },
    { id: 'safari', icon: '🦁', bg: 'linear-gradient(135deg, #ff9800 0%, #ffc107 50%, #8bc34a 100%)' },
    { id: 'rainbow', icon: '🌈', bg: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 25%, #48dbfb 50%, #ff9ff3 75%, #54a0ff 100%)' },
    { id: 'sports', icon: '⚽', bg: 'linear-gradient(135deg, #4caf50 0%, #2196f3 50%, #ff9800 100%)' },
    { id: 'art', icon: '🎨', bg: 'linear-gradient(135deg, #e91e63 0%, #9c27b0 25%, #673ab7 50%, #3f51b5 75%, #2196f3 100%)' },
    { id: 'music', icon: '🎵', bg: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 50%, #ff5722 100%)' },
    { id: 'carnival', icon: '🎪', bg: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 25%, #ff9ff3 50%, #54a0ff 75%, #5f27cd 100%)' },
    { id: 'beach', icon: '🏖️', bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 50%, #ffd89b 100%)' },
    { id: 'garden', icon: '🌸', bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 25%, #fecfef 50%, #a8edea 75%, #fed6e3 100%)' },
    { id: 'movie', icon: '🎬', bg: 'linear-gradient(135deg, #2c3e50 0%, #fd79a8 50%, #fdcb6e 100%)' }
  ], []);

  // Filter themes based on search term
  const filteredThemes = useMemo(() => {
    if (!searchTerm) return themes;
    return themes.filter(theme =>
      t(`gallery.themes.${theme.id}.title`).toLowerCase().includes(searchTerm.toLowerCase()) ||
      t(`gallery.themes.${theme.id}.desc`).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, t, themes]);

  const handleThemeClick = (themeId: string, background: string) => {
    setSelectedTheme(themeId);
    // Apply background to themes section
    const themesSection = document.querySelector('.theme-categories') as HTMLElement;
    if (themesSection) {
      themesSection.style.background = background;
    }
  };

  const handleCallPartyDirector = () => {
    window.location.href = 'tel:+14073982139';
  };

  const handleInstagramClick = () => {
    window.open('https://instagram.com/cabannas_party', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="gallery">
      <div className="container">
        <div className="gallery-header">
          <h1>{t('gallery.title')}</h1>
          <p>{t('gallery.subtitle')}</p>
        </div>

        {/* Instagram Integration Section */}
        <section className="instagram-section">
          <div className="instagram-info">
            <h2>{t('gallery.instagram.title')}</h2>
            <p>{t('gallery.instagram.desc')}</p>
            <Button
              text={t('gallery.instagram.button')}
              emoji="📷"
              onClick={handleInstagramClick}
              theme="light"
              size="large"
            />
          </div>

          {/* Instagram Embed Widget */}
          <div className="instagram-embed">
            <div className="instagram-feed">
              <iframe
                src="https://www.instagram.com/cabannas_party/embed/"
                width="100%"
                height="600"
                frameBorder="0"
                scrolling="yes"
                allowTransparency={true}
                style={{
                  border: 'none',
                  borderRadius: '15px',
                  overflow: 'hidden',
                  background: 'white'
                }}
                title="Instagram Feed - Cabanna's Party"
                loading="lazy"
                allow="encrypted-media"
              />
            </div>
          </div>
        </section>

        {/* Tent Types Section */}
        <section className="tent-types">
          <div className="container">
            <h2>{t('gallery.tent.types.title')}</h2>
            <p className="tent-types-subtitle">{t('gallery.tent.types.subtitle')}</p>

            <div className="tent-types-grid">
              <div className="tent-type-card">
                <div className="tent-type-icon">🎪</div>
                <h3>{t('gallery.tent.canopy.title')}</h3>
                <p>{t('gallery.tent.canopy.desc')}</p>
                <div className="tent-features">
                  <span className="feature">🌟 Open Space</span>
                  <span className="feature">🏠 Indoor Flexible</span>
                  <span className="feature">🎨 Custom Decor</span>
                </div>
              </div>

              <div className="tent-type-card featured">
                <div className="popular-badge">Most Popular</div>
                <div className="tent-type-icon">⛺</div>
                <h3>{t('gallery.tent.small.title')}</h3>
                <p>{t('gallery.tent.small.desc')}</p>
                <div className="tent-features">
                  <span className="feature">✨ Cozy Setup</span>
                  <span className="feature">🛏️ Individual Areas</span>
                  <span className="feature">💡 Fairy Lights</span>
                </div>
              </div>

              <div className="tent-type-card">
                <div className="tent-type-icon">🏕️</div>
                <h3>{t('gallery.tent.big.title')}</h3>
                <p>{t('gallery.tent.big.desc')}</p>
                <div className="tent-features">
                  <span className="feature">🌲 Outdoor Glamping</span>
                  <span className="feature">☀️ Weather Resistant</span>
                  <span className="feature">🌈 Premium Experience</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Theme Categories */}
        <section className="theme-categories">
          <h2>{t('gallery.themes.title')}</h2>

          {/* Search Bar */}
          <div className="theme-search">
            <input
              type="text"
              placeholder={t('gallery.themes.search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <div className="search-icon">🔍</div>
          </div>

          {/* Custom Theme Note */}
          <div className="custom-theme-note">
            <p>{t('gallery.themes.custom.note')}</p>
          </div>

          {/* Themes Grid */}
          <div className="categories-grid">
            {filteredThemes.map((theme) => (
              <div
                key={theme.id}
                className={`category-card ${selectedTheme === theme.id ? 'selected' : ''}`}
                onClick={() => handleThemeClick(theme.id, theme.bg)}
                style={selectedTheme === theme.id ? { background: theme.bg } : {}}
              >
                <div className="category-icon">{theme.icon}</div>
                <h3>{t(`gallery.themes.${theme.id}.title`)}</h3>
                <p>{t(`gallery.themes.${theme.id}.desc`)}</p>
              </div>
            ))}
          </div>

          {filteredThemes.length === 0 && (
            <div className="no-results">
              <p>{t('gallery.themes.no.results').replace('{query}', searchTerm)}</p>
            </div>
          )}
        </section>

        {/* Setup Process with CTA */}
        <section className="setup-process">
          <div className="container">
            <h2>{t('gallery.process.title')}</h2>
            <p className="process-subtitle">{t('gallery.process.subtitle')}</p>

            <div className="process-steps">
              <div className="step">
                <div className="step-circle">
                  <div className="step-number">1</div>
                  <div className="step-icon">📞</div>
                </div>
                <div className="step-content">
                  <h4>{t('gallery.process.step1.title')}</h4>
                  <p>{t('gallery.process.step1.desc')}</p>
                </div>
                <div className="step-connector"></div>
              </div>

              <div className="step">
                <div className="step-circle">
                  <div className="step-number">2</div>
                  <div className="step-icon">🏕️</div>
                </div>
                <div className="step-content">
                  <h4>{t('gallery.process.step2.title')}</h4>
                  <p>{t('gallery.process.step2.desc')}</p>
                </div>
                <div className="step-connector"></div>
              </div>

              <div className="step">
                <div className="step-circle">
                  <div className="step-number">3</div>
                  <div className="step-icon">🎉</div>
                </div>
                <div className="step-content">
                  <h4>{t('gallery.process.step3.title')}</h4>
                  <p>{t('gallery.process.step3.desc')}</p>
                </div>
                <div className="step-connector"></div>
              </div>

              <div className="step">
                <div className="step-circle">
                  <div className="step-number">4</div>
                  <div className="step-icon">🧹</div>
                </div>
                <div className="step-content">
                  <h4>{t('gallery.process.step4.title')}</h4>
                  <p>{t('gallery.process.step4.desc')}</p>
                </div>
              </div>
            </div>

            {/* CTA Section within Setup Process */}
            <CTASection
              title={t('gallery.cta.title')}
              subtitle={t('gallery.cta.subtitle')}
              buttonText={t('gallery.cta.button')}
              buttonEmoji="📞"
              onButtonClick={handleCallPartyDirector}
              textColor="light"
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default Gallery;
